import './styles.scss'

import React, { useState, useEffect, useRef } from 'react'
import { useActions, useValues } from 'kea'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import Twicpics from 'lib/tags/twicpics'
import displayProfileName from 'lib/utils/display-profile-name'
import railsContext from 'lib/selectors/rails-context'
import Share from 'lib/icons/share'
import GoodFit from 'lib/components/new-good-fit'
import { reorderArabicText } from 'lib/utils/reorder-arabic-text'
import I18n from 'lib/i18n'

import ReviewLine from '~/scenes/course/show/review-line'
import VideoPlayer from '~/scenes/shared/new-video-player'

import FavoriteButton from '../favorite-button'

import verifiedBadgeIcon from '../_assets/verified-icon.svg'
import dotIcon from '../_assets/dot-icon.svg'
import superInstructorIcon from '../_assets/super-instructor-icon.svg'
import memberSinceIcon from '../_assets/member-since-icon.svg'
import repeatStudentsIcon from '../_assets/repeat-students-icon.svg'

import Lightbox from '~/scenes/shared/profile/profile-header/lightbox'

import lightBoxLogic from '~/scenes/shared/profile/profile-header/lightbox/logic'
import courseShowLogic from '../logic'

const MAX_CHARACTERS = 14

export default function ProfileHeader ({
  observerRef,
  course,
  user,
  video,
  avatar,
  enableSharing,
  reputation,
  enableGoodFitModal
}) {
  const adminLinksAreaHeight = useRef(null)
  const { closeVideo, setCurrentImage } = useActions(lightBoxLogic({ user }))
  const { isMobile, isTablet, isDesktopWithInBetween } = useValues(courseShowLogic)

  const {
    dpr,
    featureFlags: { isAdminUser }
  } = useSelector(railsContext)

  const [isMobileBreakpoint, setIsMobileBreakpoint] = useState(false)
  const [isTabletBreakpoint, setIsTabletBreakpoint] = useState(false)
  const [isDesktopWithInBetweenBreakpoint, setIsDesktopWithInBetweenBreakpoint] = useState(false)

  const {
    firstName,
    url,
    avatarUrl,
    photos,
    reviewCount,
    reviewSum,
    avgReview100,
    isPremium,
    avatarAlt: userAvatarAlt
  } = user

  const { avatarAlt: courseAvatarAlt, recentlyActive } = course || {}

  const { memberSince, repeatStudentCount } = reputation

  const profileName = displayProfileName(firstName, MAX_CHARACTERS)

  const reviewText = I18n.t('courses.show.reviews', { count: reviewCount })

  const isVideoListed = video && video.state === 'listed'

  useEffect(() => {
    setIsMobileBreakpoint(isMobile)
  }, [isMobile])

  useEffect(() => {
    setIsTabletBreakpoint(isTablet)
  }, [isTablet])

  useEffect(() => {
    setIsDesktopWithInBetweenBreakpoint(isDesktopWithInBetween)
  }, [isDesktopWithInBetween])

  function isContainArabic (str) {
    if (!str) return false

    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/

    return arabicRegex.test(str)
  }

  useEffect(() => {
    const adminLinksArea = window.document.querySelector('.admin-links')
    if (adminLinksArea) {
      adminLinksAreaHeight.current = adminLinksArea.offsetHeight
    }
  })

  const handleClick = event => {
    event.preventDefault()
    setCurrentImage(0)
  }

  return (
    <div
      className={`profile-header-container${isVideoListed ? ' with-video' : ''}`}
      style={{ paddingTop: isAdminUser ? adminLinksAreaHeight.current + 8 : 0 }}
      ref={observerRef}
    >
      <div className={`result profile-widget-mobile${isVideoListed ? '' : ' without-video'}`}>
        <div className={`image-name-reviews-reputation-container${isVideoListed ? ' with-video' : ''}`}>
          <div className='profile-image-container' onClick={handleClick}>
            <Twicpics
              className='custom'
              src={avatar || avatarUrl}
              width='100%'
              alt={courseAvatarAlt || userAvatarAlt}
              dpr={dpr}
            />

            <div className='favorite-button-container'>
              <FavoriteButton />
            </div>

            <div className='share-button-container'>
              <Share onClick={enableSharing} />
            </div>

            {photos && photos.length > 1 && (
              <div className='photo-counter'>
                <span>{`1/${photos && photos.length + 1}`}</span>
              </div>
            )}

            <Lightbox user={user} />
          </div>

          <div className='name-reviews-reputation-section'>
            <div className='name-reviews-section'>
              <div className='name-verified-badge-status-container'>
                <div className='name-verified-badge-container'>
                  <a className={`name mobile-name${isContainArabic(profileName) ? ' contain-arabic' : ''}`} href={url}>
                    {profileName}
                  </a>

                  <div className='verified-badge-container'>
                    <img
                      className='verified-badge-icon'
                      alt='verified badge'
                      src={`${verifiedBadgeIcon}`}
                      width={19}
                      height={19}
                    />
                  </div>
                </div>

                {recentlyActive && (
                  <div className='status-container'>
                    <div className='dot-icon-container'>
                      <img className='dot-icon' alt='dot icon' src={`${dotIcon}`} width={8} height={8} />
                    </div>
                    <div className='status-text'>{I18n.t('react.search.result.recently_active')}</div>
                  </div>
                )}
              </div>

              {!isDesktopWithInBetweenBreakpoint && reviewCount > 0 && (
                <div className='review-line-mobile-container'>
                  <ReviewLine
                    avgReview100={avgReview100}
                    reviewText={reviewText}
                    reviewCount={reviewCount}
                    reviewSum={reviewSum}
                  />
                </div>
              )}
            </div>

            <div className='reputation-section'>
              {isPremium && (
                <div className='super-instructor'>
                  <div className='super-instructor-icon'>
                    <img
                      className='super-instructor-icon'
                      alt='super instructor icon'
                      src={`${superInstructorIcon}`}
                      width={24}
                      height={24}
                    />
                  </div>
                  <div className='title-description'>
                    <div className='title'>{I18n.t('profiles.pro_badge')}</div>
                    <div className='description'>{I18n.t('profiles.profile_layout.premium_hint')}</div>
                  </div>
                </div>
              )}

              <div className='member-since'>
                <div className='member-since-icon'>
                  <img
                    className='member-since-icon'
                    alt='member since icon'
                    src={`${memberSinceIcon}`}
                    width={24}
                    height={24}
                  />
                </div>
                <div className='title-description'>
                  <div className='title'>{I18n.t('profiles.profile_layout.since', { day: memberSince })}</div>
                  <div className='description'>
                    {I18n.t('profiles.profile_layout.teacher_since', { day: memberSince })}
                  </div>
                </div>
              </div>

              {!!repeatStudentCount && (
                <div className='repeat-students'>
                  <div className='repeat-students-icon'>
                    <img
                      className='repeat-students-icon'
                      alt='repeat students icon'
                      src={`${repeatStudentsIcon}`}
                      width={24}
                      height={24}
                    />
                  </div>
                  <div className='title-description'>
                    <div className='title'>
                      {reorderArabicText(
                        I18n.t('profiles.profile_layout.repeat_students', { count: repeatStudentCount })
                      )}
                    </div>
                    <div className='description'>
                      {I18n.t('profiles.profile_layout.return_students', { count: repeatStudentCount })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {isMobileBreakpoint && <GoodFit onClick={enableGoodFitModal} />}

        {(isMobileBreakpoint || isTabletBreakpoint) && (
          <VideoPlayer
            isVisible={isVideoListed}
            videoId={video.uid}
            title={firstName}
            closeVideo={closeVideo}
            withGradient
          />
        )}
      </div>
    </div>
  )
}

ProfileHeader.propTypes = {
  course: PropTypes.object,
  observerRef: PropTypes.object,
  user: PropTypes.object,
  video: PropTypes.object,
  avatar: PropTypes.string,
  enableSharing: PropTypes.func,
  enableGoodFitModal: PropTypes.func,
  reputation: PropTypes.object
}
